// The honesty ledger, read from the trace rather than written here.
//
// A project that states its limits precisely makes a stronger claim than one that does not mention them.
// This table is generated by the C++ tool from the run itself, so it cannot drift into flattery: if a
// number was not measured, the file says so and this panel repeats it.
//
// Grouped, and the grouping is the fix for a real misreading
// ----------------------------------------------------------
// The first version listed all six rows together and drew `not-measurable` in the colour it uses for lost
// data. A reader took that to mean the project was unfinished — which is the opposite of what those rows
// say. They are the claims this project *refuses to make*, because a cloud VM has no PMU counters and AWS
// has no multicast on an ordinary VPC, and a latency figure produced anyway would be a number with nothing
// behind it. Saying so is the strongest thing on the page, so it is headed as such and drawn in ink rather
// than in red.

import type { Limit, LimitStatus, Trace } from "../model/trace";

const GROUPS: ReadonlyArray<{
  readonly of: LimitStatus;
  readonly heading: string;
  readonly why: string;
}> = [
  {
    of: "measured",
    heading: "Measured in this run",
    why: "Counted from the run itself rather than asserted in prose.",
  },
  {
    of: "simulated",
    heading: "Simulated, and labelled as such",
    why: "A manual clock the driver advances. That is what makes the run reproducible, and it is not a measurement.",
  },
  {
    of: "not-measurable",
    heading: "Deliberately not claimed",
    why: "Not unfinished work. These cannot be measured honestly on the hardware this runs on, so no number is given rather than a number with nothing behind it.",
  },
];

interface Props {
  readonly trace: Trace;
}

export function Ledger({ trace }: Props) {
  return (
    <section className="panel ledger">
      <h2>What this run measured, and what it does not claim</h2>
      <p className="why">
        Generated by <code>tools/trace</code> from the run, not written by hand.
      </p>

      {GROUPS.map((group) => {
        const rows = trace.header.limits.filter((limit: Limit) => limit.status === group.of);
        if (rows.length === 0) {
          return null;
        }
        return (
          <div key={group.of} className="ledger__group">
            <h3 className="ledger__heading mono">{group.heading}</h3>
            <p className="ledger__why">{group.why}</p>
            <table>
              <tbody>
                {rows.map((limit: Limit) => (
                  <tr key={limit.claim}>
                    <td>{limit.claim}</td>
                    <td className="ledger__note">{limit.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </section>
  );
}
